import { create } from "zustand";

interface ValidationResult {
    status: "Align" | "Almost Align" | "Not Align";
    message: string;
    validated_objectives: string[];
}

interface CustomQuestionResult {
    original_question: string;
    status: "Align" | "Almost Align" | "Not Align";
    message: string;
    validated_questions: string[];
}

type Screen = "main" | "objective" | "plan" | "dashboard";

interface InstantAIDashboardActions {
    setScreen: (screen: Screen) => void;
    setFile: (file: File | null) => void;
    setExpectedDomain: (domain: string | null) => void;
    setCustomObjective: (objective: string) => void;
    setIsValidating: (loading: boolean) => void;
    setValidationResult: (result: ValidationResult | null) => void;
    setIsSuggesting: (loading: boolean) => void;
    setSuggestedQuestions: (questions: string[]) => void;
    setRationale: (rationale: string) => void;
    setIsPlanning: (loading: boolean) => void;
    setListOfCharts: (charts: Record<string, any[]>) => void;
    setConfirmedQuestions: (questions: string[]) => void;

    setSelectedQs: (questions: string[]) => void;
    setCustomInput: (input: string) => void;
    setIsCustomValidating: (loading: boolean) => void;
    setCustomResult: (result: CustomQuestionResult | null) => void;
    setCustomQuestions: (questions: string[] | ((prev: string[]) => string[])) => void;
    setSelectedCustomQs: (questions: string[]) => void;
    setAlmostAlignPicks: (picks: number[]) => void;

    resetAll: () => void;
}

interface InstantAIDashboardState {
    // index.tsx state
    screen: Screen;
    file: File | null;
    expectedDomain: string | null;
    customObjective: string;
    isValidating: boolean;
    validationResult: ValidationResult | null;
    isSuggesting: boolean;
    suggestedQuestions: string[];
    rationale: string;
    isPlanning: boolean;
    listOfCharts: Record<string, any[]>;
    confirmedQuestions: string[];

    // ObjectiveScreen.tsx state
    selectedQs: string[]; // Store as array for easier zustand handling
    customInput: string;
    isCustomValidating: boolean;
    customResult: CustomQuestionResult | null;
    customQuestions: string[];
    selectedCustomQs: string[];
    almostAlignPicks: number[];

    actions: InstantAIDashboardActions;
}

const initialState = {
    screen: "main" as Screen,
    file: null,
    expectedDomain: null,
    customObjective: "",
    isValidating: false,
    validationResult: null,
    isSuggesting: false,
    suggestedQuestions: [],
    rationale: "",
    isPlanning: false,
    listOfCharts: {},
    confirmedQuestions: [],

    selectedQs: [],
    customInput: "",
    isCustomValidating: false,
    customResult: null,
    customQuestions: [],
    selectedCustomQs: [],
    almostAlignPicks: [],
};

const useInstantAIDashboardStore = create<InstantAIDashboardState>((set) => ({
    ...initialState,
    actions: {
        setScreen: (screen) => set({ screen }),
        setFile: (file) => set({ file }),
        setExpectedDomain: (expectedDomain) => set({ expectedDomain }),
        setCustomObjective: (customObjective) => set({ customObjective }),
        setIsValidating: (isValidating) => set({ isValidating }),
        setValidationResult: (validationResult) => set({ validationResult }),
        setIsSuggesting: (isSuggesting) => set({ isSuggesting }),
        setSuggestedQuestions: (suggestedQuestions) => set({ suggestedQuestions }),
        setRationale: (rationale) => set({ rationale }),
        setIsPlanning: (isPlanning) => set({ isPlanning }),
        setListOfCharts: (listOfCharts) => set({ listOfCharts }),
        setConfirmedQuestions: (confirmedQuestions) => set({ confirmedQuestions }),

        setSelectedQs: (selectedQs) => set({ selectedQs }),
        setCustomInput: (customInput) => set({ customInput }),
        setIsCustomValidating: (isCustomValidating) => set({ isCustomValidating }),
        setCustomResult: (customResult) => set({ customResult }),
        setCustomQuestions: (customQuestions) =>
            set((state) => ({
                customQuestions: typeof customQuestions === 'function' ? customQuestions(state.customQuestions) : customQuestions
            })),
        setSelectedCustomQs: (selectedCustomQs) => set({ selectedCustomQs }),
        setAlmostAlignPicks: (almostAlignPicks) => set({ almostAlignPicks }),

        resetAll: () => set(initialState),
    },
}));

export const useInstantAIDashboardStoreState = () => useInstantAIDashboardStore((state) => state);
export const useInstantAIDashboardActions = () => useInstantAIDashboardStore((state) => state.actions);
